import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, doc, deleteDoc, query, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar";
import { toast } from "sonner";

import {
  ClipboardList, Search, MapPin, Clock, Eye, 
  Trash2, Star, ShieldCheck, Home, FileText, 
  User, UserCog, Calendar, Hash, Zap, MessageSquare
} from "lucide-react";

export default function AdminOrders() {
  const navigate = useNavigate();
  const { authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      toast.error("Erro ao carregar ordens.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchesStatus = statusFilter === "all" ? true : o.status === statusFilter;
      const term = search.toLowerCase();
      return matchesStatus && (
        (o.clientName || "").toLowerCase().includes(term) ||
        (o.technicianName || "").toLowerCase().includes(term) ||
        (o.serviceArea || "").toLowerCase().includes(term) ||
        (o.serviceCode || "").toLowerCase().includes(term)
      );
    });
  }, [orders, search, statusFilter]);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'open': return { label: 'Aberta', color: 'bg-blue-100 text-blue-700' };
      case 'waiting_approval': return { label: 'Em Análise', color: 'bg-orange-100 text-orange-700' };
      case 'in_progress': return { label: 'Em Andamento', color: 'bg-indigo-100 text-indigo-700' };
      case 'completed': return { label: 'Concluída', color: 'bg-green-100 text-green-700' };
      case 'cancelled': return { label: 'Cancelada', color: 'bg-red-100 text-red-700' };
      default: return { label: status, color: 'bg-slate-100 text-slate-700' };
    }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400 italic">SOCORRE AÍ • CARREGANDO...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Gestão de <span className="text-blue-600">Ordens</span></h1>
              <div className="flex flex-wrap items-center gap-2 mt-4 text-left">
                  {[
                    {id: 'all', label: 'Todas'},
                    {id: 'open', label: 'Abertas'},
                    {id: 'waiting_approval', label: 'Análise'},
                    {id: 'in_progress', label: 'Andamento'},
                    {id: 'completed', label: 'Concluídas'},
                    {id: 'cancelled', label: 'Canceladas'}
                  ].map(f => (
                    <button 
                     key={f.id} 
                     onClick={() => setStatusFilter(f.id)} 
                     className={`text-[9px] font-black uppercase px-4 py-2 rounded-full transition-all ${statusFilter === f.id ? `bg-slate-900 text-white shadow-lg` : 'bg-white text-slate-400 border border-slate-100 hover:border-blue-200'}`}
                    >
                      {f.label}
                    </button>
                  ))}
              </div>
            </div>
            <div className="relative w-full md:w-96 group text-left">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
              <input 
                type="text" 
                placeholder="BUSCAR CLIENTE, PROFISSIONAL OU CÓDIGO..." 
                className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-xs uppercase italic outline-none focus:ring-2 focus:ring-blue-500/20" 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
          </header>

          <div className="grid gap-4 text-left">
            {loading ? (
                <div className="py-20 text-center font-black text-slate-300 animate-pulse uppercase italic tracking-widest">Sincronizando Banco de Dados...</div>
            ) : filteredOrders.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-100 uppercase font-black text-slate-300 italic">Nenhum registro encontrado.</div>
            ) : filteredOrders.map((order) => {
              const isOpen = selectedOrder === order.id;
              const status = getStatusInfo(order.status);
              return (
                <Card key={order.id} className={`rounded-[2.5rem] border-none shadow-xl transition-all overflow-hidden ${isOpen ? 'ring-2 ring-blue-500' : ''}`}>
                  <CardContent className="p-0 text-left">
                    <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-6">
                      <div className="flex items-center gap-5 flex-1">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${order.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-slate-50 text-slate-400'}`}>
                          <ClipboardList size={24} />
                        </div>
                        <div className="text-left">
                          <div className="flex flex-wrap items-center gap-2">
                             <h3 className="text-lg font-black text-slate-900 uppercase italic tracking-tighter leading-none">{order.serviceArea}</h3>
                             <Badge className={`${status.color} border-none font-black text-[8px] uppercase px-2 py-0.5 italic`}>{status.label}</Badge>
                          </div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase italic mt-1">
                             <span className="text-blue-600">Cliente: {order.clientName}</span> • <span className="text-orange-600">Profissional: {order.technicianName || 'Pendente'}</span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                         <div className="text-right hidden md:block mr-4">
                            <p className="text-[9px] font-black text-slate-300 uppercase leading-none mb-1">Cód. OS</p>
                            <p className="text-xs font-mono font-black text-slate-900">#{order.serviceCode || order.id.slice(-6).toUpperCase()}</p>
                         </div>
                         <Button onClick={() => setSelectedOrder(isOpen ? null : order.id)} className="h-12 rounded-xl font-black uppercase text-[10px] bg-slate-900 text-white hover:bg-blue-600 transition-all gap-2 px-6">
                           {isOpen ? "Recolher" : "Ver Tudo"} <Eye size={14}/>
                         </Button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="p-8 bg-white border-t border-slate-50 animate-in slide-in-from-top-2 text-left">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            
                           {/* CLIENTE & LOCALIZAÇÃO */}
                           <div className="space-y-6">
                              <h4 className="text-[10px] font-black uppercase text-blue-600 flex items-center gap-2 italic"><User size={14}/> Dados do Solicitante</h4>
                              <div className="p-6 bg-blue-50/50 rounded-[2rem] border border-blue-100 space-y-4">
                                  <div>
                                     <p className="font-black text-blue-900 uppercase italic text-sm">{order.clientName}</p>
                                     <p className="text-[10px] font-bold text-blue-400 uppercase flex items-center gap-1 mt-1"><Zap size={10}/> ID do Usuário: {order.clientId?.slice(0,12)}...</p>
                                  </div>
                                  <div className="pt-4 border-t border-blue-100 space-y-3">
                                    <div className="flex items-start gap-2 text-left">
                                      <MapPin size={14} className="text-red-500 shrink-0 mt-0.5"/>
                                      <p className="text-[10px] font-bold text-slate-600 uppercase leading-relaxed">
                                         {order.address}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Home size={14} className="text-blue-400"/>
                                      <p className="text-[10px] font-black text-blue-700 uppercase italic">{order.propertyType} • Urgência: {order.urgency}</p>
                                    </div>
                                  </div>
                              </div>
                           </div>

                           {/* PROFISSIONAL & EXECUÇÃO */}
                           <div className="space-y-6">
                              <h4 className="text-[10px] font-black uppercase text-orange-600 flex items-center gap-2 italic"><UserCog size={14}/> Profissional Designado</h4>
                              <div className="p-6 bg-orange-50/50 rounded-[2rem] border border-orange-100 space-y-4">
                                  {order.technicianId ? (
                                    <>
                                       <div>
                                          <p className="font-black text-orange-900 uppercase italic text-sm">{order.technicianName}</p>
                                          <p className="text-[10px] font-bold text-orange-400 uppercase mt-1">Status: {status.label}</p>
                                       </div>
                                       <div className="pt-4 border-t border-orange-100">
                                          <div className="flex items-center gap-2 text-green-600">
                                             <ShieldCheck size={14}/>
                                             <p className="text-[10px] font-black uppercase italic">Vínculo Seguro • Token #{order.serviceCode}</p>
                                          </div>
                                       </div>
                                    </>
                                  ) : (
                                    <div className="py-4 text-center">
                                       <Clock size={24} className="text-orange-300 mx-auto mb-2 animate-pulse"/>
                                       <p className="text-[10px] font-black text-slate-400 uppercase italic">Aguardando aceite de profissional</p>
                                    </div>
                                  )}
                              </div>

                              {/* AVALIAÇÃO (SÓ APARECE SE TIVER) */}
                              {order.rating > 0 && (
                                <div className="space-y-3">
                                   <h4 className="text-[10px] font-black uppercase text-yellow-600 flex items-center gap-2 italic"><Star size={14}/> Feedback do Cliente</h4>
                                   <div className="p-5 bg-yellow-50 rounded-[2rem] border border-yellow-100">
                                      <div className="flex gap-1 mb-2">
                                         {[...Array(5)].map((_, i) => (
                                           <Star key={i} size={12} className={`${i < order.rating ? 'fill-yellow-500 text-yellow-500' : 'text-slate-200'}`} />
                                         ))}
                                      </div>
                                      <p className="text-[11px] font-bold text-slate-700 italic leading-relaxed">"{order.ratingComment || 'Sem comentário escrito.'}"</p>
                                   </div>
                                </div>
                              )}
                           </div>

                           {/* LOGS & DESCRIÇÃO */}
                           <div className="space-y-6">
                              <h4 className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2 italic"><FileText size={14}/> Histórico do Chamado</h4>
                              <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4">
                                  <div>
                                      <Label className="text-[9px] font-black uppercase text-slate-400 italic">Relato do Problema:</Label>
                                      <p className="text-[11px] font-bold text-slate-600 italic leading-relaxed mt-1">"{order.description}"</p>
                                  </div>
                                  <div className="pt-4 border-t border-slate-200 space-y-2">
                                     <div className="flex items-center justify-between text-[9px] font-black uppercase italic">
                                        <span className="text-slate-400">Criado em:</span>
                                        <span className="text-slate-600">{new Date(order.createdAt).toLocaleString('pt-BR')}</span>
                                     </div>
                                     {order.completedAt && (
                                       <div className="flex items-center justify-between text-[9px] font-black uppercase italic">
                                          <span className="text-green-500">Concluído em:</span>
                                          <span className="text-green-600">{new Date(order.completedAt).toLocaleString('pt-BR')}</span>
                                       </div>
                                     )}
                                     {order.cancelledAt && (
                                       <div className="flex items-center justify-between text-[9px] font-black uppercase italic">
                                          <span className="text-red-500">Cancelado em:</span>
                                          <span className="text-red-600">{new Date(order.cancelledAt).toLocaleString('pt-BR')}</span>
                                       </div>
                                     )}
                                  </div>
                              </div>
                           </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-10 border-t mt-10">
                            <Button 
                               variant="destructive" 
                               onClick={() => { if(window.confirm("Excluir esta OS permanentemente do banco de dados?")) deleteDoc(doc(db, "orders", order.id)).then(loadData) }} 
                               className="h-14 w-14 rounded-2xl shadow-lg active:scale-95 transition-all"
                            >
                               <Trash2 size={20} />
                            </Button>
                            <Button 
                               onClick={() => setSelectedOrder(null)} 
                               className="h-14 px-10 bg-slate-900 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl active:scale-95 transition-all"
                            >
                               VOLTAR À LISTAGEM
                            </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </main>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }` }} />
    </div>
  );
}